import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosObj } from "../../../../config/apiClients";
import { AccountMappingState } from "../../../types";

interface AddNeoId {
  userId: string;
  neoId: string;
}

const initialState: AccountMappingState = {
  isLoading: false,
  accountMappings: [],
  neoUsersAccount: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    hasNextPage: false,
    totalItems: 0,
  }
};

interface NeoUsersParams {
  search?: string;
  paramPage?: number;
  paramLimit?: number;
}

export const getAllNeoUsers = createAsyncThunk(
  "/neo/v2/accountMapping/getAllUsers",
  async (
    { search, paramPage = 1, paramLimit = 20 }: NeoUsersParams = {},
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("paramPage", String(paramPage));
      params.set("paramLimit", String(paramLimit));
      if (search) params.set("search", search);

      const response = await axiosObj.get(
        `neo/v2/accountMapping/getAllUsers?${params.toString()}`,
      );
      return response.data;
    } catch (error: any) {
      // Backend returns 404 when nothing matches — treat as an empty page.
      if (error?.response?.status === 404) {
        return {
          payload: {
            allNeoUsers: [],
            totalItems: 0,
            page: paramPage,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: paramPage > 1,
          },
        };
      }
      return rejectWithValue(error || { message: 'Failed to Fetch Neo Users' });
    }
  }
);

export const assignNeoId = createAsyncThunk(
  "/neo/v2/accountMapping/addNeoId",
  async (addNeoId: AddNeoId, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch("neo/v2/accountMapping/addNeoId", addNeoId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Assign NEO ID to User' });
    }
  }
);

export const getAllNeoUsersAccount = createAsyncThunk(
  "/neo/v2/accountMapping/getAllNeoUsersAccount",
  async (search: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`neo/v2/accountMapping/getAllNeoUsersAccount${search ? `?search=${search}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Neo Users Account' });
    }
  }
);

export const deleteNeoAccount = createAsyncThunk(
  "/neo/v2/accountMapping/delete",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete('neo/v2/accountMapping/delete', { data: { userId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Neo Users Account' });
    }
  }
);


const accountMappingSlice = createSlice({
  name: "accountMappings",
  initialState,
  reducers: {
    removeNeoAccount: (state, action) => {
      state.accountMappings = state.accountMappings.filter((item) => item.id !== action.payload);
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getAllNeoUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllNeoUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        const p = action.payload?.payload ?? {};
        state.accountMappings = Array.isArray(p.allNeoUsers)
          ? p.allNeoUsers
          : Array.isArray(p)
            ? p
            : [];
        state.pagination = {
          currentPage: p.page ?? action.meta.arg?.paramPage ?? 1,
          totalPages: p.totalPages ?? 1,
          hasNextPage: !!p.hasNextPage,
          totalItems: p.totalItems ?? state.accountMappings.length,
        };
      })
      .addCase(getAllNeoUsers.rejected, (state) => {
        state.isLoading = false;
        state.accountMappings = [];
        state.pagination = {
          currentPage: 1,
          totalPages: 0,
          hasNextPage: false,
          totalItems: 0,
        };
      })
      .addCase(assignNeoId.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(assignNeoId.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(assignNeoId.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAllNeoUsersAccount.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllNeoUsersAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.neoUsersAccount = action.payload.payload.kAllNeoUserAccounts
      })
      .addCase(getAllNeoUsersAccount.rejected, (state) => {
        state.isLoading = false;
        state.neoUsersAccount = []
      });
  }
});

export const { removeNeoAccount } = accountMappingSlice.actions;

export default accountMappingSlice.reducer;